import {
  combineReducers,
  configureStore,
  createListenerMiddleware,
  ThunkAction,
  UnknownAction,
} from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';

import {
  baseBackofficeApi,
  baseBillingBackofficeApi,
  baseIdeaasBackofficeApi,
} from '@/backoffice/redux/api/base-api';
import { mapReducer } from '@/console/features/map/store/map.slice';
import { permissionsListener } from '@/shared/features/permissions/store/permissions.listener';
import permissionsReducer from '@/shared/features/permissions/store/permissions.slice';
import authReducer, { authListener } from '@/shared/redux/api/auth/auth-api';
import {
  baseAssetsApi,
  baseAuditApi,
  baseBillingApi,
  baseIdeaasApi,
  baseIdeaasV2Api,
  baseIdentApi,
  baseIdentPublicApi,
  baseIntegrationApi,
  baseJobsApi,
  baseNotificationsApi,
  baseOffboardingApi,
  baseOpenplcApi,
  baseSdaiApi,
  baseTelemetryApi,
  baseVscodeApi,
} from '@/shared/redux/api/base-api';
import { renderingApi } from '@/shared/redux/api/rendering/rendering-api';
import {
  backgroundRefetchReducer,
  clearBackgroundMode,
} from '@/shared/redux/background-refetch/background-refetch.slice';
import { toastsReducer } from '@/shared/redux/toasts/toasts.slice';

const APPSYNC_AFFECTED_PATHS = [
  baseJobsApi.reducerPath,
  baseAssetsApi.reducerPath,
  baseAuditApi.reducerPath,
  baseIdentApi.reducerPath,
];

const backgroundRefetchListener = createListenerMiddleware();

backgroundRefetchListener.startListening({
  predicate: (action, currentState) => {
    const state = currentState as {
      backgroundRefetch: { isBackgroundMode: boolean };
    };
    if (!state.backgroundRefetch.isBackgroundMode) return false;
    const type = action.type as string;
    return APPSYNC_AFFECTED_PATHS.some(
      (path) =>
        type.startsWith(`${path}/`) &&
        (type.endsWith('/fulfilled') || type.endsWith('/rejected')),
    );
  },
  effect: (_action, listenerApi) => {
    const state = listenerApi.getState() as AppState;
    const isAnyStillPending = APPSYNC_AFFECTED_PATHS.some((path) => {
      const apiState = state[path as keyof AppState] as
        | { queries: Record<string, { status: string }> }
        | undefined;
      return Object.values(apiState?.queries ?? {}).some(
        (query) => query?.status === 'pending',
      );
    });
    if (!isAnyStillPending) {
      listenerApi.dispatch(clearBackgroundMode());
    }
  },
});

const appReducer = combineReducers({
  auth: authReducer,
  backgroundRefetch: backgroundRefetchReducer,
  map: mapReducer,
  permissions: permissionsReducer,
  toasts: toastsReducer,
  [baseAssetsApi.reducerPath]: baseAssetsApi.reducer,
  [baseIdentApi.reducerPath]: baseIdentApi.reducer,
  [baseIdentPublicApi.reducerPath]: baseIdentPublicApi.reducer,
  [baseJobsApi.reducerPath]: baseJobsApi.reducer,
  [baseTelemetryApi.reducerPath]: baseTelemetryApi.reducer,
  [renderingApi.reducerPath]: renderingApi.reducer,
  [baseIdeaasApi.reducerPath]: baseIdeaasApi.reducer,
  [baseIdeaasV2Api.reducerPath]: baseIdeaasV2Api.reducer,
  [baseVscodeApi.reducerPath]: baseVscodeApi.reducer,
  [baseOpenplcApi.reducerPath]: baseOpenplcApi.reducer,
  [baseSdaiApi.reducerPath]: baseSdaiApi.reducer,
  [baseNotificationsApi.reducerPath]: baseNotificationsApi.reducer,
  [baseOffboardingApi.reducerPath]: baseOffboardingApi.reducer,
  [baseAuditApi.reducerPath]: baseAuditApi.reducer,
  [baseIntegrationApi.reducerPath]: baseIntegrationApi.reducer,
  [baseBillingApi.reducerPath]: baseBillingApi.reducer,
  [baseBackofficeApi.reducerPath]: baseBackofficeApi.reducer,
  [baseBillingBackofficeApi.reducerPath]: baseBillingBackofficeApi.reducer,
  [baseIdeaasBackofficeApi.reducerPath]: baseIdeaasBackofficeApi.reducer,
});

export const store = configureStore({
  reducer: appReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .concat(backgroundRefetchListener.middleware)
      .concat(authListener.middleware)
      .concat(permissionsListener.middleware)
      .concat(baseAssetsApi.middleware)
      .concat(baseIdentApi.middleware)
      .concat(baseIdentPublicApi.middleware)
      .concat(baseJobsApi.middleware)
      .concat(baseTelemetryApi.middleware)
      .concat(renderingApi.middleware)
      .concat(baseIdeaasApi.middleware)
      .concat(baseIdeaasV2Api.middleware)
      .concat(baseVscodeApi.middleware)
      .concat(baseOpenplcApi.middleware)
      .concat(baseNotificationsApi.middleware)
      .concat(baseSdaiApi.middleware)
      .concat(baseOffboardingApi.middleware)
      .concat(baseAuditApi.middleware)
      .concat(baseIntegrationApi.middleware)
      .concat(baseBillingApi.middleware)
      .concat(baseBackofficeApi.middleware)
      .concat(baseBillingBackofficeApi.middleware)
      .concat(baseIdeaasBackofficeApi.middleware),
});

setupListeners(store.dispatch);

export default store;
export type AppState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  AppState,
  unknown,
  UnknownAction
>;
